// ---------------------------------------------------------------------------
// Prop Model Loader — GLB-based prop model loading with caching and cloning.
//
// Mirrors the viewmodel.ts GLB loading pattern: one master copy per prop type
// is cached, and each instance receives a clone(). Falls back gracefully to
// null on load failure so callers can use procedural meshes instead.
//
// Reuses the shared GLTFLoader + DRACOLoader from map-asset-loader.ts.
// ---------------------------------------------------------------------------

import * as THREE from 'three';
import { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';
import { getMapGltfLoader } from './map-asset-loader';
import { MAP_PROP_MODELS, type MapPropModelConfig } from './map-prop-models';
import { TEXTURE_SET_REGISTRY } from '../renderer/texture-loader';
import { getRenderer } from '../renderer/scene';
import type { GraphicsQuality } from '../core/settings';

// ---------------------------------------------------------------------------
// Shared Texture Cache — KTX2 PBR textures shared across prop models.
// Loaded once per texture set, reused by all props referencing the same set.
// ---------------------------------------------------------------------------

interface SharedPBRTextures {
  color: THREE.Texture | null;
  normal: THREE.Texture | null;
  orm: THREE.Texture | null;
}

/** Global cache: one set of PBR textures per texture-set name. */
const sharedTextureCache = new Map<string, SharedPBRTextures>();
/** In-flight loads to prevent double-loading the same texture set. */
const sharedTextureLoading = new Map<string, Promise<SharedPBRTextures | null>>();
/** Lazily initialized KTX2Loader for shared prop textures. */
let sharedKtx2Loader: KTX2Loader | null = null;

function getSharedKtx2Loader(): KTX2Loader | null {
  if (sharedKtx2Loader) return sharedKtx2Loader;
  let renderer: THREE.WebGLRenderer;
  try {
    renderer = getRenderer();
  } catch {
    return null;
  }
  sharedKtx2Loader = new KTX2Loader();
  sharedKtx2Loader.setTranscoderPath('/libs/basis/');
  sharedKtx2Loader.detectSupport(renderer);
  return sharedKtx2Loader;
}

function loadKtx2Texture(loader: KTX2Loader, url: string, maxAnisotropy: number): Promise<THREE.Texture> {
  return new Promise<THREE.Texture>((resolve, reject) => {
    loader.load(
      url,
      (texture) => {
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.minFilter = THREE.LinearMipmapLinearFilter;
        texture.generateMipmaps = false;
        texture.anisotropy = maxAnisotropy;
        resolve(texture);
      },
      undefined,
      (error) => reject(error),
    );
  });
}

/**
 * Load a shared PBR texture set by name. Returns cached textures if already
 * loaded, or loads them from KTX2 files and caches for reuse.
 */
async function loadSharedTextureSet(setName: string): Promise<SharedPBRTextures | null> {
  // Return from cache
  const cached = sharedTextureCache.get(setName);
  if (cached) return cached;

  // Join in-flight load
  const inflight = sharedTextureLoading.get(setName);
  if (inflight) return inflight;

  const def = TEXTURE_SET_REGISTRY[setName];
  if (!def) {
    console.warn(`[PropModelLoader] unknown texture set "${setName}"`);
    return null;
  }

  const ktx2 = getSharedKtx2Loader();
  if (!ktx2) {
    console.warn('[PropModelLoader] renderer not available, skipping shared textures');
    return null;
  }

  let maxAniso = 4;
  try {
    maxAniso = getRenderer().capabilities.getMaxAnisotropy();
  } catch { /* use default */ }

  const loadPromise = (async (): Promise<SharedPBRTextures | null> => {
    try {
      const [color, normal, orm] = await Promise.all([
        def.hasColor ? loadKtx2Texture(ktx2, def.path + 'color.ktx2', maxAniso) : Promise.resolve(null),
        def.hasNormal ? loadKtx2Texture(ktx2, def.path + 'normal.ktx2', maxAniso) : Promise.resolve(null),
        def.hasORM ? loadKtx2Texture(ktx2, def.path + 'orm.ktx2', maxAniso) : Promise.resolve(null),
      ]);

      // Apply registry-defined UV repeat to all loaded textures
      const repeat = def.repeat ?? [1, 1];
      for (const tex of [color, normal, orm]) {
        if (tex) tex.repeat.set(repeat[0], repeat[1]);
      }

      // Color map should be sRGB
      if (color) color.colorSpace = THREE.SRGBColorSpace;
      // Normal and ORM are linear data
      if (normal) normal.colorSpace = THREE.LinearSRGBColorSpace;
      if (orm) orm.colorSpace = THREE.LinearSRGBColorSpace;

      const set: SharedPBRTextures = { color, normal, orm };
      sharedTextureCache.set(setName, set);
      return set;
    } catch (err) {
      console.warn(`[PropModelLoader] failed to load shared texture set "${setName}":`, err);
      return null;
    } finally {
      sharedTextureLoading.delete(setName);
    }
  })();

  sharedTextureLoading.set(setName, loadPromise);
  return loadPromise;
}

/** Dispose all shared prop textures. Call during scene teardown. */
function disposeSharedTextureCache(): void {
  for (const [, set] of sharedTextureCache) {
    set.color?.dispose();
    set.normal?.dispose();
    set.orm?.dispose();
  }
  sharedTextureCache.clear();
  sharedTextureLoading.clear();
  if (sharedKtx2Loader) {
    sharedKtx2Loader.dispose();
    sharedKtx2Loader = null;
  }
}

// ---------------------------------------------------------------------------
// PropModelLoader
// ---------------------------------------------------------------------------

class PropModelLoader {
  /** Master copy cache: one loaded Group per prop type (never added to scene). */
  private cache = new Map<string, THREE.Group>();
  /** In-flight loads — prevents double-loading the same type. */
  private loading = new Map<string, Promise<THREE.Group | null>>();

  // -----------------------------------------------------------------------
  // Memory budget tracking
  // -----------------------------------------------------------------------

  /** Estimated total memory used by all cached master copies (KB). */
  private memoryUsageKB = 0;
  /** Per-type memory estimate (KB) for budget tracking on unload. */
  private memoryPerType = new Map<string, number>();
  /** Maximum memory budget for prop GLBs (KB). 2 MB default. */
  static readonly MAX_MEMORY_KB = 2048;

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /**
   * Load a prop model by type name. Returns a **clone** of the cached master
   * copy, or null if the type has no GLB config or the load fails.
   *
   * First call for a given type triggers the actual load; subsequent calls
   * return clones from cache immediately.
   */
  async loadPropModel(type: string): Promise<THREE.Group | null> {
    // Fast path: already cached
    const cached = this.cache.get(type);
    if (cached) {
      return this.cloneMaster(cached, type);
    }

    // In-flight load — wait for it, then clone
    const inflight = this.loading.get(type);
    if (inflight) {
      const result = await inflight;
      return result ? this.cloneMaster(result, type) : null;
    }

    // No config for this type — no GLB available
    const config = MAP_PROP_MODELS[type];
    if (!config) return null;

    // Start the load
    const loadPromise = this.doLoad(type, config);
    this.loading.set(type, loadPromise);

    const master = await loadPromise;
    this.loading.delete(type);

    return master ? this.cloneMaster(master, type) : null;
  }

  /**
   * Preload all prop types at a given priority level.
   * Call with 'blocking' at init, 'eager' during freeze time, etc.
   *
   * When `quality` is provided, respects mobile quality tiers:
   * - mobile-low: skips ALL GLB preloading (procedural fallback only)
   * - mobile-medium / low: skips lazy-priority GLB preloading
   */
  async preloadByPriority(priority: 'blocking' | 'eager' | 'lazy', quality?: GraphicsQuality): Promise<void> {
    // No GLB preloading on mobile-low — use procedural fallbacks only
    if (quality === 'mobile-low') return;
    // Skip lazy-priority GLBs on low-end devices
    if ((quality === 'mobile-medium' || quality === 'low') && priority === 'lazy') return;

    const types = Object.entries(MAP_PROP_MODELS)
      .filter(([, cfg]) => cfg.priority === priority)
      .map(([type]) => type);

    if (types.length === 0) return;

    await Promise.all(types.map((type) => this.ensureLoaded(type)));
  }

  /**
   * Check whether a prop type has a GLB model configured (regardless of
   * whether it has been loaded yet).
   */
  hasModelConfig(type: string): boolean {
    return type in MAP_PROP_MODELS;
  }

  /**
   * Check whether a prop type's master copy is already in cache.
   */
  isLoaded(type: string): boolean {
    return this.cache.has(type);
  }

  /**
   * Get current estimated memory usage of all cached master copies (KB).
   */
  getMemoryUsageKB(): number {
    return this.memoryUsageKB;
  }

  /**
   * Returns true if loading another GLB would exceed the memory budget.
   */
  isOverBudget(): boolean {
    return this.memoryUsageKB >= PropModelLoader.MAX_MEMORY_KB;
  }

  /**
   * Dispose a single cached master copy by type, freeing its GPU resources
   * and reclaiming memory budget. Used by distance-based unloading.
   * Returns true if the type was cached and disposed.
   */
  disposeType(type: string): boolean {
    const master = this.cache.get(type);
    if (!master) return false;

    this.disposeMaster(master);
    this.cache.delete(type);

    const typeKB = this.memoryPerType.get(type) ?? 0;
    this.memoryUsageKB = Math.max(0, this.memoryUsageKB - typeKB);
    this.memoryPerType.delete(type);

    return true;
  }

  /**
   * Dispose all cached models, freeing GPU resources.
   * Call during scene teardown / map change.
   */
  dispose(): void {
    for (const [, master] of this.cache) {
      this.disposeMaster(master);
    }
    this.cache.clear();
    this.loading.clear();
    this.memoryUsageKB = 0;
    this.memoryPerType.clear();
    disposeSharedTextureCache();
  }

  // -----------------------------------------------------------------------
  // Internal
  // -----------------------------------------------------------------------

  /**
   * Ensure a type's master copy is in cache (load if needed).
   * Unlike loadPropModel, this does NOT return a clone.
   */
  private async ensureLoaded(type: string): Promise<void> {
    if (this.cache.has(type)) return;

    const inflight = this.loading.get(type);
    if (inflight) {
      await inflight;
      return;
    }

    const config = MAP_PROP_MODELS[type];
    if (!config) return;

    const loadPromise = this.doLoad(type, config);
    this.loading.set(type, loadPromise);
    await loadPromise;
    this.loading.delete(type);
  }

  /**
   * Actually load a GLB and store the master copy in cache.
   * Respects memory budget — returns null if budget would be exceeded.
   */
  private async doLoad(type: string, config: MapPropModelConfig): Promise<THREE.Group | null> {
    // Budget check before loading (skip for blocking priority — always load)
    if (config.priority !== 'blocking' && this.isOverBudget()) {
      return null;
    }

    const glbPath = config.glbPath;

    try {
      const loader = getMapGltfLoader();
      const gltf = await loader.loadAsync(glbPath);
      const model = gltf.scene;

      // Apply uniform scale from manifest
      if (config.scale !== 1) {
        model.scale.setScalar(config.scale);
      }

      // Apply rotation offset from manifest
      if (config.rotationOffset[0] !== 0 || config.rotationOffset[1] !== 0 || config.rotationOffset[2] !== 0) {
        model.rotation.set(...config.rotationOffset);
      }

      // Enable shadows on all meshes
      model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });

      // Apply shared PBR textures if configured
      if (config.textureSet) {
        await this.applySharedTextures(model, config.textureSet);
      }

      // Track memory usage
      const memKB = this.estimateMemoryKB(model);
      this.memoryUsageKB += memKB;
      this.memoryPerType.set(type, memKB);

      this.cache.set(type, model);
      return model;
    } catch (_err) {
      // Silent degradation — caller falls back to procedural mesh
      console.warn(`[PropModelLoader] failed to load ${type} from ${glbPath}, falling back to procedural`);
      return null;
    }
  }

  /**
   * Estimate memory usage of a loaded model.
   * ~32 bytes per vertex (position + normal + uv + index overhead).
   */
  private estimateMemoryKB(model: THREE.Group): number {
    let bytes = 0;
    model.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        const posAttr = child.geometry.getAttribute('position');
        if (posAttr) bytes += posAttr.count * 32;
      }
    });
    return bytes / 1024;
  }

  /**
   * Apply shared PBR textures from a named texture set to all
   * MeshStandardMaterial children in the model. Shared textures are loaded
   * once and cached globally, so multiple prop types referencing the same
   * set share the same GPU texture objects (VRAM savings).
   */
  private async applySharedTextures(model: THREE.Group, setName: string): Promise<void> {
    const textures = await loadSharedTextureSet(setName);
    if (!textures) return;

    const def = TEXTURE_SET_REGISTRY[setName];

    model.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      const materials = Array.isArray(child.material) ? child.material : [child.material];
      for (const mat of materials) {
        if (!(mat instanceof THREE.MeshStandardMaterial)) continue;

        // Only dispose the old map if it isn't itself a shared texture from a
        // prior applySharedTextures() call (e.g. two props loaded from the
        // same GLB). Disposing a shared texture wrecks every other material
        // that still references it.
        if (textures.color) {
          if (mat.map && mat.map !== textures.color && !this.isSharedTexture(mat.map)) {
            mat.map.dispose();
          }
          mat.map = textures.color;
          // Reset color factor so texture is not tinted
          mat.color.setHex(0xffffff);
        }

        if (textures.normal) {
          if (mat.normalMap && mat.normalMap !== textures.normal && !this.isSharedTexture(mat.normalMap)) {
            mat.normalMap.dispose();
          }
          mat.normalMap = textures.normal;
          const nScale = def?.normalScale ?? 1.0;
          mat.normalScale.set(nScale, nScale);
        }

        if (textures.orm) {
          if (mat.aoMap && mat.aoMap !== textures.orm && !this.isSharedTexture(mat.aoMap)) {
            mat.aoMap.dispose();
          }
          mat.aoMap = textures.orm;
          mat.aoMapIntensity = def?.aoIntensity ?? 1.0;

          if (mat.roughnessMap && mat.roughnessMap !== textures.orm && !this.isSharedTexture(mat.roughnessMap)) {
            mat.roughnessMap.dispose();
          }
          mat.roughnessMap = textures.orm;
          mat.roughness = 1.0;

          if (mat.metalnessMap && mat.metalnessMap !== textures.orm && !this.isSharedTexture(mat.metalnessMap)) {
            mat.metalnessMap.dispose();
          }
          mat.metalnessMap = textures.orm;
          mat.metalness = 1.0;
        }

        mat.needsUpdate = true;
      }
    });
  }

  /**
   * Clone a master Group for instancing. Uses deep clone() which copies
   * geometry references (shared) and clones materials.
   */
  private cloneMaster(master: THREE.Group, _type: string): THREE.Group {
    const cloned = master.clone(true);

    // Ensure shadow flags propagate to cloned meshes
    cloned.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    return cloned;
  }

  /**
   * Dispose a master Group's GPU resources (geometries + materials).
   */
  private disposeMaster(group: THREE.Group): void {
    group.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      child.geometry?.dispose();

      if (Array.isArray(child.material)) {
        for (const mat of child.material) {
          this.disposeMaterial(mat);
        }
      } else if (child.material) {
        this.disposeMaterial(child.material);
      }
    });
  }

  /**
   * Check if a texture is owned by the shared texture cache (should not be
   * disposed individually — the cache manages their lifetime).
   */
  private isSharedTexture(tex: THREE.Texture | null): boolean {
    if (!tex) return false;
    for (const [, set] of sharedTextureCache) {
      if (tex === set.color || tex === set.normal || tex === set.orm) return true;
    }
    return false;
  }

  /**
   * Dispose a single material and its map textures.
   * Skips textures owned by the shared texture cache.
   */
  private disposeMaterial(material: THREE.Material): void {
    // Dispose all map textures on standard/physical materials
    if (material instanceof THREE.MeshStandardMaterial) {
      if (!this.isSharedTexture(material.map)) material.map?.dispose();
      if (!this.isSharedTexture(material.normalMap)) material.normalMap?.dispose();
      if (!this.isSharedTexture(material.roughnessMap)) material.roughnessMap?.dispose();
      if (!this.isSharedTexture(material.metalnessMap)) material.metalnessMap?.dispose();
      if (!this.isSharedTexture(material.aoMap)) material.aoMap?.dispose();
      material.emissiveMap?.dispose();
    }
    material.dispose();
  }
}

// ---------------------------------------------------------------------------
// Singleton
// ---------------------------------------------------------------------------

let instance: PropModelLoader | null = null;

/** Get or create the global PropModelLoader instance. */
export function getPropModelLoader(): PropModelLoader {
  if (!instance) {
    instance = new PropModelLoader();
  }
  return instance;
}

/** Dispose the global PropModelLoader. */
export function disposePropModelLoader(): void {
  instance?.dispose();
  instance = null;
}

/** Dispose all prop-related resources including shared texture cache. */
export function disposeAllPropResources(): void {
  disposePropModelLoader();
  disposeSharedTextureCache();
}
